import React, { useState, useRef, useEffect } from 'react';
import styles from './FormField.module.css';

const ICONS = {
  user: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  mail: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  ),
  phone: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  calendar: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  chevron: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  upload: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 21h14" />
    </svg>
  ),
};

// Custom dropdown that matches the design (styled trigger + popover of rounded
// option cards). Used for the `select` field type.
function SelectField({ name, value, options, placeholder, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className={styles.select} ref={ref}>
      <button
        type="button"
        id={name}
        className={`${styles.selectTrigger} ${open ? styles.selectTriggerOpen : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected ? styles.selectValue : styles.selectPlaceholder}>
          {selected ? selected.label : placeholder || 'Select an option'}
        </span>
        <span className={styles.selectChevron}>{ICONS.chevron}</span>
      </button>
      {open && (
        <ul className={styles.selectPopover} role="listbox">
          {options.map((opt) => {
            const isSel = opt.value === value;
            return (
              <li key={opt.value} role="option" aria-selected={isSel}>
                <button
                  type="button"
                  className={`${styles.selectOption} ${isSel ? styles.selectOptionSelected : ''}`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FormField({ field, value, otherValue, onChange }) {
  const {
    name,
    label,
    type,
    options,
    placeholder,
    required,
    helperText,
    showCharCount,
    footerLeftText,
    icon,
    trailingIcon,
    columns,
    variant,
    hint,
    maxSelect,
    allowOther,
  } = field;

  const handleChange = (e) => {
    const newValue = type === 'checkbox' ? e.target.checked : e.target.value;
    onChange(name, newValue);
  };

  const handleMultiselectToggle = (optionValue) => {
    const current = Array.isArray(value) ? value : [];
    if (current.includes(optionValue)) {
      onChange(name, current.filter((v) => v !== optionValue));
      return;
    }
    // Respect the max-selection cap (e.g. "Select up to 3 countries").
    if (maxSelect && current.length >= maxSelect) return;
    onChange(name, [...current, optionValue]);
  };

  const otherInput = (
    <input
      type="text"
      className={styles.otherInput}
      placeholder="Please specify"
      value={otherValue || ''}
      onChange={(e) => onChange(`${name}Other`, e.target.value)}
    />
  );

  const renderInput = () => {
    switch (type) {
      case 'select':
        return (
          <SelectField
            name={name}
            value={value}
            options={options}
            placeholder={placeholder}
            onChange={(v) => onChange(name, v)}
          />
        );

      case 'multiselect': {
        const selectedArr = Array.isArray(value) ? value : [];
        const atLimit = maxSelect && selectedArr.length >= maxSelect;
        return (
          <>
            <div
              className={`${styles.multiselectGrid} ${name === 'opportunityType' ? styles.trackGrid : ''}`}
              style={
                columns
                  ? { gridTemplateColumns: `repeat(${columns}, 1fr)` }
                  : undefined
              }
            >
              {options.map((opt) => {
                const selected = selectedArr.includes(opt.value);
                const disabled = !selected && atLimit;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    className={`${styles.multiselectOption} ${opt.isOther ? styles.multiselectOtherOption : ''} ${selected ? styles.multiselectOptionSelected : ''} ${disabled ? styles.multiselectOptionDisabled : ''}`}
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={(e) => {
                      const optionButton = e.currentTarget;
                      handleMultiselectToggle(opt.value);
                      if (opt.isOther && !selected) {
                        setTimeout(() => optionButton.querySelector('input')?.focus(), 0);
                      }
                    }}
                  >
                    {opt.icon && (
                      <span className={styles.optionIcon}>{opt.icon}</span>
                    )}
                    {opt.countryCode && (
                      <span
                        className={`fi fis fi-${opt.countryCode} ${styles.flag}`}
                      />
                    )}
                    <span className={styles.multiselectLabel}>
                      {opt.isOther ? `${opt.label}:` : opt.label}
                    </span>
                    {opt.isOther && (
                      <input
                        type="text"
                        className={styles.otherInputInline}
                        placeholder=""
                        value={otherValue || ''}
                        autoFocus={selected}
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!selected) handleMultiselectToggle(opt.value);
                        }}
                        onChange={(e) => onChange(`${name}Other`, e.target.value)}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </>
        );
      }

      case 'radio':
        if (variant !== 'list') {
          return (
            <>
              <div
                className={styles.radioCardGrid}
                style={columns ? { ['--radio-cols']: columns } : undefined}
              >
                {options.map((opt) => {
                  const selected = value === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`${styles.radioCard} ${selected ? styles.radioCardSelected : ''} ${opt.isOther ? styles.radioCardFull : ''}`}
                    >
                      <input
                        type="radio"
                        name={name}
                        value={opt.value}
                        checked={selected}
                        onChange={handleChange}
                        required={required}
                      />
                      <span>{opt.isOther ? `${opt.label}:` : opt.label}</span>
                    </label>
                  );
                })}
              </div>
              {allowOther && value === 'other' && otherInput}
            </>
          );
        }
        return (
          <>
            <div className={styles.radioGroup}>
              {options.map((opt) => (
                <label key={opt.value} className={styles.radioOption}>
                  <input
                    type="radio"
                    name={name}
                    value={opt.value}
                    checked={value === opt.value}
                    onChange={handleChange}
                    required={required}
                  />
                  <span className={styles.radioText}>
                    <span className={styles.radioLabel}>{opt.label}</span>
                    {opt.sublabel && (
                      <span className={styles.radioSublabel}>{opt.sublabel}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {allowOther && value === 'other' && otherInput}
          </>
        );

      case 'checkbox':
        return (
          <label className={styles.checkboxOption}>
            <input
              type="checkbox"
              id={name}
              name={name}
              checked={!!value}
              onChange={handleChange}
            />
            {placeholder}
          </label>
        );

      case 'textarea':
        return (
          <>
            <textarea
              className={styles.textarea}
              id={name}
              name={name}
              value={value || ''}
              onChange={handleChange}
              placeholder={placeholder}
              required={required}
            />
            {(showCharCount || footerLeftText) && (
              <div className={styles.textareaFooter}>
                <span>{footerLeftText || ''}</span>
                {showCharCount && <span>{(value || '').length} chars</span>}
              </div>
            )}
          </>
        );

      case 'file':
        return (
          <label className={styles.fileUpload}>
            <div className={styles.fileUploadIcon}>{ICONS.upload}</div>
            <p className={styles.fileUploadTitle}>{label}</p>
            <p className={styles.fileUploadHint}>
              {hint || 'PDF, DOC or DOCX · Max 10 MB'}
            </p>
            <span className={styles.fileUploadLink}>
              Click to browse, or drag and drop
            </span>
            <input
              type="file"
              id={name}
              name={name}
              accept={field.accept}
              onChange={(e) => onChange(name, e.target.files[0])}
              style={{ display: 'none' }}
            />
            {value && <p className={styles.fileName}>{value.name}</p>}
          </label>
        );

      default:
        return (
          <div className={styles.inputWrapper}>
            {icon && <span className={styles.inputIcon}>{ICONS[icon]}</span>}
            <input
              className={`${styles.input} ${icon ? styles.inputWithLeadingIcon : ''} ${trailingIcon ? styles.inputWithTrailingIcon : ''}`}
              type={type}
              id={name}
              name={name}
              value={value || ''}
              onChange={handleChange}
              placeholder={placeholder}
              required={required}
            />
            {trailingIcon && (
              <span className={styles.inputTrailingIcon}>
                {ICONS[trailingIcon]}
              </span>
            )}
          </div>
        );
    }
  };

  return (
    <div className={styles.field}>
      {type !== 'checkbox' && type !== 'file' && (
        <label htmlFor={name} className={styles.label}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
      )}
      {type === 'file' && (
        <label className={styles.label}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
      )}
      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {renderInput()}
    </div>
  );
}

export default FormField;
